import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Button, Divider, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Modal } from '@repletoryreach/sdk-ui/overlays';
import { Switch, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { BOOKINGS_KEY, bookingErrorText, serviceColour, statusLabel, statusTone, useDateLocale, useServices } from '../lib/data';
import { formatInZone, localDateOf } from '../lib/time';
import { AvailabilityPick, AvailabilityPicker } from './AvailabilityPicker';

const MUTED = 'var(--rr-color-text-muted)';

type Dialog = null | 'move' | 'cancel' | 'decline';

/**
 * One booking: when, who, what they answered, where it came from and what has
 * happened to it, with the actions its state allows. Opened from the calendar,
 * the list and a notice's link (`/bookings/{id}`).
 */
export function BookingDrawer({ bookingId, onClose, tz }: { bookingId: string | null; onClose: () => void; tz: string }) {
  const t = useT();
  const qc = useQueryClient();
  const locale = useDateLocale();
  const servicesQ = useServices(true);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [note, setNote] = useState('');

  useEffect(() => { setDialog(null); setNote(''); }, [bookingId]);

  const q = useQuery({
    queryKey: [BOOKINGS_KEY, 'booking', bookingId],
    queryFn: () => bookings.getBooking(bookingId!),
    enabled: !!bookingId
  });
  const b = q.data?.data;
  const service = servicesQ.data?.data.find((s) => s.id === b?.service.id) ?? null;

  const done = (msgKey: string) => (res: { data: bookings.Booking }) => {
    void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
    notifySuccess(t(msgKey, { number: res.data.number }), t('bookings.title'));
    setDialog(null);
  };
  const failed = (e: unknown) => notifyError(bookingErrorText(t, e), t('bookings.title'));

  const approve = useMutation({ mutationFn: () => bookings.approveBooking(b!.id), onSuccess: done('bookings.toast.approved'), onError: failed });
  const complete = useMutation({ mutationFn: () => bookings.completeBooking(b!.id), onSuccess: done('bookings.toast.completed'), onError: failed });
  const noShow = useMutation({ mutationFn: () => bookings.markNoShow(b!.id), onSuccess: done('bookings.toast.noShow'), onError: failed });
  const addNote = useMutation({
    mutationFn: () => bookings.addBookingNote(b!.id, note.trim()),
    onSuccess: (res) => { setNote(''); done('bookings.toast.noteAdded')(res); },
    onError: failed
  });

  const when = (iso: string) => formatInZone(iso, tz, locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const endTime = (iso: string) => formatInZone(iso, tz, locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

  const open = b && (b.status === 'requested' || b.status === 'confirmed');
  const started = b ? new Date(b.starts_at).getTime() <= Date.now() : false;
  const answers = b?.answers ? Object.entries(b.answers) : [];
  const questionLabel = (key: string) => service?.questions.find((qq) => qq.key === key)?.label ?? key;

  return (
    <Drawer opened={!!bookingId} onClose={onClose} title={b ? t('bookings.drawer.title', { number: b.number }) : t('bookings.drawer.loading')} size="lg">
      <Stack gap="lg" data-testid="booking-drawer">
        {q.error && (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{bookingErrorText(t, q.error)}</span>
              <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        {q.isPending && <Skeleton height={160} />}

        {b && (
          <>
            <Stack gap="xs">
              <Group gap="sm" wrap="wrap" align="center">
                <span className="rr-bookings__swatch" style={{ background: serviceColour(service?.colour) }} aria-hidden />
                <Title order={4}>{b.service.name}</Title>
                <StatusBadge status={statusTone(b.status)}>{statusLabel(t, b.status)}</StatusBadge>
              </Group>
              <Text role="bodySmall">{t('bookings.drawer.when', { start: when(b.starts_at), end: endTime(b.ends_at) })}</Text>
              {b.staff && <Text role="bodySmall" c={MUTED}>{t('bookings.drawer.with', { name: b.staff.name })}</Text>}
            </Stack>

            {open && (
              <Group gap="xs" wrap="wrap">
                {b.status === 'requested' && (
                  <>
                    <Button size="xs" loading={approve.isPending} onClick={() => approve.mutate()}>{t('bookings.action.approve')}</Button>
                    <Button size="xs" variant="default" onClick={() => setDialog('decline')}>{t('bookings.action.decline')}</Button>
                  </>
                )}
                <Button size="xs" variant="default" onClick={() => setDialog('move')}>{t('bookings.action.move')}</Button>
                {b.status === 'confirmed' && started && (
                  <>
                    <Button size="xs" variant="default" loading={complete.isPending} onClick={() => complete.mutate()}>{t('bookings.action.complete')}</Button>
                    <Button size="xs" variant="default" loading={noShow.isPending} onClick={() => noShow.mutate()}>{t('bookings.action.noShow')}</Button>
                  </>
                )}
                <Button size="xs" variant="subtle" color="red" onClick={() => setDialog('cancel')}>{t('bookings.action.cancel')}</Button>
              </Group>
            )}

            <Divider />

            <Stack gap="xs">
              <Text role="overline" c={MUTED}>{t('bookings.field.client')}</Text>
              <Text role="body" fw={600}>{b.contact.name || t('common.unknown')}</Text>
              {b.contact.phone && <Text role="bodySmall">{b.contact.phone}</Text>}
              {b.contact.email && <Text role="bodySmall">{b.contact.email}</Text>}
              {b.address && <Text role="bodySmall" c={MUTED}>{b.address}</Text>}
              <Group gap="sm" wrap="wrap">
                {b.conversation_id && (
                  <Link to={`/inbox?conversation=${encodeURIComponent(b.conversation_id)}`}>
                    <Button size="xs" variant="default">{t('bookings.drawer.openConversation')}</Button>
                  </Link>
                )}
                {b.contact.id && (
                  <Link to={`/contacts?contact=${encodeURIComponent(b.contact.id)}`}>
                    <Button size="xs" variant="subtle">{t('bookings.drawer.openContact')}</Button>
                  </Link>
                )}
              </Group>
            </Stack>

            {answers.length > 0 && (
              <Stack gap="xs">
                <Text role="overline" c={MUTED}>{t('bookings.field.answers')}</Text>
                {answers.map(([k, v]) => (
                  <Group key={k} gap="sm" wrap="nowrap" align="flex-start" justify="space-between">
                    <Text role="bodySmall" c={MUTED}>{questionLabel(k)}</Text>
                    <Text role="bodySmall" style={{ textAlign: 'right' }}>
                      {typeof v === 'boolean' ? (v ? t('common.yes') : t('common.no')) : String(v ?? '')}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}

            <Stack gap="xs">
              <Text role="overline" c={MUTED}>{t('common.details')}</Text>
              <Group gap="sm" justify="space-between">
                <Text role="bodySmall" c={MUTED}>{t('bookings.field.channel')}</Text>
                <Text role="bodySmall">{t(`bookings.channel.${b.channel ?? 'console'}`, b.channel ?? '')}</Text>
              </Group>
              <Group gap="sm" justify="space-between">
                <Text role="bodySmall" c={MUTED}>{t('bookings.field.source')}</Text>
                <Text role="bodySmall">{t(`bookings.source.${b.source ?? 'console'}`, b.source ?? '')}</Text>
              </Group>
              <Group gap="sm" justify="space-between">
                <Text role="bodySmall" c={MUTED}>{t('common.created')}</Text>
                <Text role="bodySmall">{formatDateTime(b.created_at)}</Text>
              </Group>
              {b.notes && (
                <Stack gap={2}>
                  <Text role="bodySmall" c={MUTED}>{t('bookings.field.notes')}</Text>
                  <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{b.notes}</Text>
                </Stack>
              )}
            </Stack>

            <Stack gap="xs">
              <Textarea
                label={t('bookings.drawer.addNote')}
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                minRows={2}
                maxLength={2000}
                description={t('bookings.drawer.addNoteHint')}
              />
              <div>
                <Button size="xs" variant="default" disabled={!note.trim()} loading={addNote.isPending} onClick={() => addNote.mutate()}>
                  {t('bookings.drawer.saveNote')}
                </Button>
              </div>
            </Stack>

            <Stack gap="xs">
              <Text role="overline" c={MUTED}>{t('bookings.drawer.history')}</Text>
              {b.history.length === 0 ? (
                <Text role="bodySmall" c={MUTED}>{t('bookings.drawer.noHistory')}</Text>
              ) : (
                <ol className="rr-bookings__history">
                  {[...b.history].sort((x, y) => y.at.localeCompare(x.at)).map((h, i) => (
                    <li key={`${h.at}-${i}`}>
                      <Text role="bodySmall" fw={600}>{t(`bookings.history.${h.action}`, h.action)}</Text>
                      {historyDetail(h.detail) && <Text role="bodySmall">{historyDetail(h.detail)}</Text>}
                      <Text role="caption" c={MUTED} title={h.at}>
                        {[formatDateTime(h.at), h.actor].filter(Boolean).join(' · ')}
                      </Text>
                    </li>
                  ))}
                </ol>
              )}
            </Stack>
          </>
        )}
      </Stack>

      {b && dialog === 'move' && service && (
        <MoveDialog booking={b} service={service} tz={tz} onClose={() => setDialog(null)} onMoved={done('bookings.toast.moved')} />
      )}
      {b && (dialog === 'cancel' || dialog === 'decline') && (
        <ReasonDialog
          booking={b}
          kind={dialog}
          onClose={() => setDialog(null)}
          onDone={done(dialog === 'cancel' ? 'bookings.toast.cancelled' : 'bookings.toast.declined')}
        />
      )}
    </Drawer>
  );
}

/** Move to another free time, with the client told or not. A time taken meanwhile asks again. */
function MoveDialog({ booking, service, tz, onClose, onMoved }: {
  booking: bookings.Booking;
  service: bookings.Service;
  tz: string;
  onClose: () => void;
  onMoved: (res: { data: bookings.Booking }) => void;
}) {
  const t = useT();
  const [pick, setPick] = useState<AvailabilityPick | null>(null);
  const [notify, setNotify] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const keepPerson = service.kind === 'appointment' && booking.staff && pick?.staff_ids?.includes(booking.staff.id);

  const move = useMutation({
    mutationFn: () => bookings.moveBooking(booking.id, {
      starts_at: pick!.starts_at,
      session_id: pick!.session_id,
      staff_id: pick!.staff_id ?? (keepPerson ? booking.staff!.id : pick!.staff_ids?.[0]),
      notify_client: notify
    }),
    onSuccess: onMoved,
    onError: (e) => {
      setError(bookingErrorText(t, e));
      if (bookings.bookingErrorCode(e) === bookings.SLOT_TAKEN || bookings.bookingErrorCode(e) === bookings.FULL) {
        setPick(null);
        setRefreshKey((k) => k + 1);
      }
    }
  });

  return (
    <Modal opened onClose={onClose} title={t('bookings.move.title', { number: booking.number })} size="lg">
      <Stack gap="md">
        <Text role="bodySmall" c={MUTED}>{t('bookings.move.now', { when: booking.when_text })}</Text>
        <AvailabilityPicker
          service={service}
          tz={tz}
          value={pick}
          onChange={(p) => { setPick(p); setError(null); }}
          initialDate={localDateOf(booking.starts_at, tz)}
          refreshKey={refreshKey}
        />
        <Switch label={t('bookings.field.notifyClient')} description={t('bookings.move.notifyHint')} checked={notify} onChange={setNotify} />
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Group gap="sm" justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.back')}</Button>
          <Button disabled={!pick} loading={move.isPending} onClick={() => move.mutate()}>{t('bookings.move.confirm')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Cancel or decline, with a reason the client may be told. */
function ReasonDialog({ booking, kind, onClose, onDone }: {
  booking: bookings.Booking;
  kind: 'cancel' | 'decline';
  onClose: () => void;
  onDone: (res: { data: bookings.Booking }) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const run = useMutation({
    mutationFn: () => kind === 'cancel'
      ? bookings.cancelBooking(booking.id, { reason: reason.trim() || undefined, notify_client: notify })
      : bookings.declineBooking(booking.id, reason.trim() || undefined),
    onSuccess: onDone,
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.title'))
  });
  return (
    <Modal opened onClose={onClose} title={t(`bookings.${kind}.title`, { number: booking.number })} size="sm">
      <Stack gap="md">
        <Text role="bodySmall">{t(`bookings.${kind}.body`, { name: booking.contact.name ?? '', when: booking.when_text })}</Text>
        <Textarea
          label={t('bookings.field.reason')}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={2}
          maxLength={500}
          description={t('bookings.reasonHint')}
        />
        {kind === 'cancel' && (
          <Switch label={t('bookings.field.notifyClient')} checked={notify} onChange={setNotify} />
        )}
        <Group gap="sm" justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.back')}</Button>
          <Button color="red" loading={run.isPending} onClick={() => run.mutate()}>{t(`bookings.${kind}.confirm`)}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * The words for a history entry's detail. The service records it as data: a reason, a note's text, or a move's
 * from and to. Nothing else in it is worth showing (the status and channel are on the booking itself).
 */
function historyDetail(detail: unknown): string | null {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail.trim() || null;
  if (typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;
  if (typeof d.text === 'string' && d.text.trim()) return d.text;
  if (typeof d.reason === 'string' && d.reason.trim()) return d.reason;
  if (typeof d.from === 'string' && typeof d.to === 'string') return `${formatDateTime(d.from)} → ${formatDateTime(d.to)}`;
  return null;
}
